import { Logger } from 'wdc-cube'
import { ValueProperty } from '../tcm_filter_manager'
import {
    JunctionMode,
    KeywordOption,
    MultipleOptionStringKeywordFilterScope,
    MultipleOptionStringKeywordParcelFilterScope
} from '../tcm_scopes'
import { MultipleStringKeywordFilter, StringKeywordOperator } from '../tcm_service'
import { FilterPaneOwner, FilterPresenter } from './tcm_filter_types'

const LOG = Logger.get('TCM-StringMultiSelectFilter')

let UID_GEN = 0

export class TcmPresenterForStringMultiSelectFilter extends FilterPresenter<
    MultipleOptionStringKeywordFilterScope,
    FilterPaneOwner
> {
    // Constructor

    constructor(
        owner: FilterPaneOwner,
        scope: MultipleOptionStringKeywordFilterScope,
        valueProperty: ValueProperty<MultipleStringKeywordFilter>,
        onSearch?: () => Promise<KeywordOption[]>
    ) {
        super(owner, scope)
        this.valueProperty = valueProperty
        this.onSearch = onSearch
    }

    // :: Fields

    protected valueProperty: ValueProperty<MultipleStringKeywordFilter>
    protected onSearch?: () => Promise<KeywordOption[]>

    acceptBlankValues = true

    // :: Getters and Setters

    get owner() {
        return super.owner as FilterPaneOwner
    }

    protected override async bindEvents() {
        this.scope.onAddFilter = this.action(this.handleAddFilter)
        this.scope.onClearAllFilters = this.action(this.handleClearAllFilters)
        LOG.debug('initialized')
    }

    override clear() {
        this.scope.conjunction.length = 0
        this.scope.disjunction.length = 0
        this.valueProperty.clear()
    }

    override publish() {
        this.valueProperty.clear()

        const emptyPredicate = (scope: MultipleOptionStringKeywordParcelFilterScope) =>
            scope.value.length === 0 && !isBlankKeywordOperator(scope.operator)
        this.scope.conjunction.removeByCriteria(emptyPredicate)
        this.scope.disjunction.removeByCriteria(emptyPredicate)

        for (const parcelScope of [...this.scope.conjunction, ...this.scope.disjunction]) {
            const isBlank = isBlankKeywordOperator(parcelScope.operator)
            const value = isBlank ? [] : parcelScope.value
            if ((!value || value.length === 0) && !isBlank) {
                continue
            }
            this.valueProperty.add(
                this.populateWithExtraValues(
                    {
                        mode: parcelScope.junctionMode,
                        operator: parcelScope.operator,
                        value
                    },
                    parcelScope
                )
            )
        }
    }

    addConjuntion() {
        const andScope = new MultipleOptionStringKeywordParcelFilterScope()
        andScope.uid = `and-${UID_GEN++}`
        andScope.junctionMode = JunctionMode.AND
        andScope.acceptBlankValues = this.acceptBlankValues
        andScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, andScope))
        andScope.onClear = this.action(this.handleClear.bind(this, andScope))
        andScope.onOptionSelected = this.action(this.handleOptionSelected.bind(this, andScope))
        andScope.getValueInOptionType = this.getValueInOptionType.bind(this, andScope)
        andScope.onReloadOptions = this.action(this.handleReloadOptions.bind(this, andScope))
        andScope.update = this.update
        this.doSearch(andScope)
        this.scope.conjunction.push(andScope)
        return andScope
    }

    addDisjuntion() {
        const orScope = new MultipleOptionStringKeywordParcelFilterScope()
        orScope.uid = `or-${UID_GEN++}`
        orScope.junctionMode = JunctionMode.OR
        orScope.acceptBlankValues = this.acceptBlankValues
        orScope.onOperatorChange = this.action(this.handleOperatorChange.bind(this, orScope))
        orScope.onClear = this.action(this.handleClear.bind(this, orScope))
        orScope.onOptionSelected = this.action(this.handleOptionSelected.bind(this, orScope))
        orScope.getValueInOptionType = this.getValueInOptionType.bind(this, orScope)
        orScope.onReloadOptions = this.action(this.handleReloadOptions.bind(this, orScope))
        orScope.update = this.update
        this.doSearch(orScope)
        this.scope.disjunction.push(orScope)
        return orScope
    }

    getsIn() {
        for (const conditionScope of this.scope.conjunction) {
            if (conditionScope.operator === StringKeywordOperator.is_in) {
                return conditionScope
            }
        }

        for (const conditionScope of this.scope.disjunction) {
            if (conditionScope.operator === StringKeywordOperator.is_in) {
                return conditionScope
            }
        }

        return null
    }

    getOrCreateIsIn() {
        let conditioScope = this.getsIn()
        if (conditioScope) {
            return conditioScope
        }

        conditioScope = this.addConjuntion()
        conditioScope.operator = StringKeywordOperator.is_in
        return conditioScope
    }

    private async handleClearAllFilters() {
        this.clear()
    }

    private async handleAddFilter(mode: JunctionMode) {
        this.mostrarItemAdicionado()

        if (mode === JunctionMode.AND) {
            this.addConjuntion()
        } else {
            this.addDisjuntion()
        }
    }

    private async handleClear(parcelScope: MultipleOptionStringKeywordParcelFilterScope) {
        let idx = this.scope.conjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.conjunction.removeByIndex(idx)
        }

        idx = this.scope.disjunction.findIndex((item) => item.uid === parcelScope.uid)
        if (idx !== -1) {
            this.scope.disjunction.removeByIndex(idx)
        }
    }

    private async handleOperatorChange(
        parcelScope: MultipleOptionStringKeywordParcelFilterScope,
        value: StringKeywordOperator
    ) {
        parcelScope.operator = value
        parcelScope.valueVisible = !isBlankKeywordOperator(value)
    }

    private doSearch(parcelScope: MultipleOptionStringKeywordParcelFilterScope) {
        if (!this.onSearch) {
            parcelScope.options.length > 0 && (parcelScope.options = [])
            return
        }

        parcelScope.optionsLoading = true
        parcelScope.optionsError = ''
        this.onSearch()
            .then((labels) => {
                parcelScope.options = labels
            })
            .catch((e) => {
                parcelScope.options = []
                parcelScope.optionsError = 'Não foi possível carregar a lista. Clique no campo para tentar de novo.'
                LOG.error(e.message)
            })
            .finally(() => {
                parcelScope.optionsLoading = false
            })
        return
    }

    private async handleReloadOptions(parcelScope: MultipleOptionStringKeywordParcelFilterScope) {
        if (parcelScope.optionsError && !parcelScope.optionsLoading) {
            this.doSearch(parcelScope)
        }
    }

    private getValueInOptionType(parcelScope: MultipleOptionStringKeywordParcelFilterScope): KeywordOption[] {
        const list = parcelScope.value
            .map((key) => parcelScope.options.find((opt) => opt.key === key))
            .filter((o) => !!o)
        return list as KeywordOption[]
    }

    private async handleOptionSelected(
        parcelScope: MultipleOptionStringKeywordParcelFilterScope,
        value: KeywordOption[]
    ) {
        parcelScope.value = value.map((v) => v.key)
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected populateWithExtraValues(
        filter: MultipleStringKeywordFilter,
        _parcelScope: MultipleOptionStringKeywordParcelFilterScope
    ) {
        return filter
    }

    apply(filters: MultipleStringKeywordFilter[] | null | undefined) {
        if (!filters || filters.length === 0) {
            return
        }

        filters.forEach((filter) => {
            const operator = toListOperator(Number(filter.operator))
            const isBlank = isBlankKeywordOperator(operator)
            const value = isBlank ? [] : toListValue(filter.value)
            if (value.length === 0 && !isBlank) {
                return
            }

            let itemScope: MultipleOptionStringKeywordParcelFilterScope
            if (filter.mode === JunctionMode.AND) {
                itemScope = this.addConjuntion()
            } else {
                itemScope = this.addDisjuntion()
            }

            itemScope.value = value
            itemScope.operator = operator
            itemScope.valueVisible = !isBlank
        })
    }
}

function toListOperator(operator: number): StringKeywordOperator {
    switch (operator) {
        case StringKeywordOperator.is_blank:
        case StringKeywordOperator.is_not_blank:
        case StringKeywordOperator.is_in:
        case StringKeywordOperator.is_not_in:
            return operator
    }
    return operator % 2 === 0 ? StringKeywordOperator.is_not_in : StringKeywordOperator.is_in
}

function toListValue(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.map((v) => `${v}`)
    }
    if (value === null || value === undefined || value === '') {
        return []
    }
    return `${value}`
        .split(/[,;]/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
}

export function isBlankKeywordOperator(operator: StringKeywordOperator) {
    return operator === StringKeywordOperator.is_blank || operator === StringKeywordOperator.is_not_blank
}
